/**
 * SEMU Search Controller - Full-Width Header Search
 *
 * Modern full-width predictive search that slides down from header.
 * Uses Shopify's native predictive search endpoints with enhanced UX.
 *
 * @version 3.0.0
 * @author SEMU Theme
 */

class SEMUPredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = new Map();
    this.currentSearchTerm = '';
    this.abortController = null;
    this.queryVersion = 0;
    this.debounceTimer = null;
    this.focusOutTimer = null;
    this.liveRegionTimer = null;
    this._bootstrapped = false;
  }

  connectedCallback() {
    if (this._bootstrapped) return;
    this._bootstrap = () => this.ensureInitialized();
    this.addEventListener('focusin', this._bootstrap);
    this._io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) this.ensureInitialized();
      },
      { rootMargin: '120px', threshold: 0 }
    );
    this._io.observe(this);
  }

  disconnectedCallback() {
    if (this._io) {
      this._io.disconnect();
      this._io = null;
    }
    if (this._bootstrap) this.removeEventListener('focusin', this._bootstrap);
    this.teardownEventListeners();
    this.invalidatePendingRequest();
    if (this.focusOutTimer) {
      clearTimeout(this.focusOutTimer);
      this.focusOutTimer = null;
    }
    if (this.liveRegionTimer) {
      clearTimeout(this.liveRegionTimer);
      this.liveRegionTimer = null;
    }
    this.input = null;
    this.results = null;
    this.liveRegion = null;
    this.errorStatus = null;
    this.errorStatusText = null;
    this._bootstrapped = false;
  }

  ensureInitialized() {
    if (this._bootstrapped) return true;

    if (!this.init()) return false;

    this._bootstrapped = true;
    if (this._io) {
      this._io.disconnect();
      this._io = null;
    }
    this.removeEventListener('focusin', this._bootstrap);
    return true;
  }

  init() {
    // The search form is a sibling of this component. Resolve it from the
    // current modal every time so a Theme Editor reconnect cannot retain a
    // detached input from the previous section instance.
    const modal = this.closest('semu-search-modal');
    this.input =
      modal?.querySelector('[data-search-input]') ||
      this.querySelector('input[type="search"], input[type="text"]');
    this.results = this.querySelector('[data-predictive-search-results]');
    this.liveRegion = this.querySelector('[data-predictive-search-live-region]');
    this.errorStatus = this.querySelector('[data-predictive-search-error]');
    this.errorStatusText = this.querySelector('[data-predictive-search-error-text]');

    if (!this.input || !this.results) {
      console.error('SEMU Search: Required input or results container not found');
      return false;
    }

    this.setupEventListeners();
    this.bindKeyboardEvents();
    return true;
  }

  setupEventListeners() {
    this._onInput = (event) => this.onChange(event);
    this._onFocus = () => this.onFocus();
    this._onFocusOut = () => this.onFocusOut();

    this.input.addEventListener('input', this._onInput);
    this.input.addEventListener('focus', this._onFocus);
    this.addEventListener('focusout', this._onFocusOut);
  }

  teardownEventListeners() {
    if (this.input) {
      if (this._onInput) this.input.removeEventListener('input', this._onInput);
      if (this._onFocus) this.input.removeEventListener('focus', this._onFocus);
      if (this._onInputKeydown) this.input.removeEventListener('keydown', this._onInputKeydown);
    }
    if (this._onFocusOut) this.removeEventListener('focusout', this._onFocusOut);

    this._onInput = null;
    this._onFocus = null;
    this._onFocusOut = null;
    this._onInputKeydown = null;
  }

  bindKeyboardEvents() {
    this._onInputKeydown = (event) => {
      switch (event.code) {
        case 'ArrowUp':
          event.preventDefault();
          this.switchOption('up');
          break;
        case 'ArrowDown':
          event.preventDefault();
          this.switchOption('down');
          break;
        case 'Enter':
          if (this.selectOption()) event.preventDefault();
          break;
        case 'Escape':
          event.preventDefault();
          this.closest('semu-search-modal')?.close();
          break;
      }
    };
    this.input.addEventListener('keydown', this._onInputKeydown);
  }

  onChange(event) {
    if (!this.ensureInitialized()) return;
    const searchTerm = event.target.value.trim();
    const searchTermChanged = searchTerm !== this.currentSearchTerm;
    const queryVersion = this.invalidatePendingRequest();
    this.currentSearchTerm = searchTerm;

    if (searchTermChanged) {
      if (this.results) this.results.innerHTML = '';
      this.setEmptyState(false);
      this.setErrorState('');
      this.removeAttribute('results');
      this.close(false);
    }

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      this.getSearchResults(searchTerm, queryVersion);
    }, 300);
  }

  onFocus() {
    if (!this.ensureInitialized()) return;
    const searchTerm = this.input.value.trim();

    if (!searchTerm.length || !this.isModalOpen()) return;

    if (this.getAttribute('results') === 'true' && this.currentSearchTerm === searchTerm) {
      this.open();
    } else {
      const queryVersion = this.invalidatePendingRequest();
      this.currentSearchTerm = searchTerm;
      this.getSearchResults(searchTerm, queryVersion);
    }
  }

  onFocusOut() {
    if (this.focusOutTimer) clearTimeout(this.focusOutTimer);
    this.focusOutTimer = setTimeout(() => {
      this.focusOutTimer = null;
      if (!this.contains(document.activeElement)) this.close();
    }, 0);
  }

  async getSearchResults(searchTerm, queryVersion = null) {
    if (!this.ensureInitialized()) return false;

    const normalizedTerm = searchTerm.trim();
    if (!normalizedTerm) return false;

    if (queryVersion === null) {
      queryVersion = this.invalidatePendingRequest();
      this.currentSearchTerm = normalizedTerm;
    }

    const modal = this.closest('semu-search-modal');
    const sessionToken = modal?.stateToken;
    // Preserve the exact trimmed query. Replacing spaces caused distinct
    // Shopify queries such as "red dress" and "red-dress" to share a cache.
    const queryKey = normalizedTerm;

    if (!this.canCommit(queryVersion, normalizedTerm, sessionToken)) return false;

    if (this.cachedResults.has(queryKey)) {
      this.hideLoading(queryVersion);
      return this.renderSearchResults(this.cachedResults.get(queryKey), normalizedTerm, queryVersion, sessionToken);
    }

    const request = new AbortController();
    this.abortController = request;
    this.showLoading(queryVersion);

    try {
      const response = await fetch(
        `${routes.predictive_search_url}?q=${encodeURIComponent(normalizedTerm)}&section_id=semu-predictive-search`,
        { signal: request.signal }
      );

      if (!response.ok) {
        const error = new Error(`Predictive search request failed with status ${response.status}`);
        error.status = response.status;
        throw error;
      }

      const text = await response.text();
      const parsedHTML = new DOMParser().parseFromString(text, 'text/html');
      const sectionElement = parsedHTML.querySelector('#shopify-section-semu-predictive-search');
      const resultsElement = sectionElement?.querySelector('[data-predictive-search-response]');

      if (!resultsElement) {
        throw new Error('Predictive search response did not contain the expected section results');
      }

      if (!this.canCommit(queryVersion, normalizedTerm, sessionToken, request)) return false;

      const totalResultsValue = resultsElement.dataset.totalResults || '';
      const parsedTotal = Number.parseInt(totalResultsValue, 10);
      const hasValidTotal = /^\d+$/.test(totalResultsValue);
      const hasRenderedOptions = Boolean(resultsElement.querySelector('[role="option"]'));

      if (!hasValidTotal || (parsedTotal > 0 && !hasRenderedOptions)) {
        throw new Error('Predictive search response contained invalid result metadata or option markup');
      }

      const resultsPayload = {
        markup: resultsElement.innerHTML,
        totalResults: parsedTotal,
      };
      this.setCachedResult(queryKey, resultsPayload);
      return this.renderSearchResults(resultsPayload, normalizedTerm, queryVersion, sessionToken);
    } catch (error) {
      const wasAborted = request.signal.aborted || error?.name === 'AbortError' || error?.code === 20;
      if (wasAborted || !this.canCommit(queryVersion, normalizedTerm, sessionToken, request)) return false;

      console.error('SEMU Search: Error fetching results', error);
      return this.renderSearchError(normalizedTerm, queryVersion, sessionToken);
    } finally {
      if (this.abortController === request) {
        this.abortController = null;
        this.hideLoading(queryVersion);
      }
    }
  }

  isModalOpen() {
    const modal = this.closest('semu-search-modal');
    return Boolean(modal?.isOpen && !modal.hidden && !modal.inert);
  }

  canCommit(queryVersion, searchTerm, sessionToken, request = null) {
    const modal = this.closest('semu-search-modal');
    return Boolean(
      this.isConnected &&
        this.input?.isConnected &&
        this.results?.isConnected &&
        modal?.isOpen &&
        modal.stateToken === sessionToken &&
        this.queryVersion === queryVersion &&
        this.input.value.trim() === searchTerm &&
        (!request || (this.abortController === request && !request.signal.aborted))
    );
  }

  invalidatePendingRequest() {
    this.queryVersion += 1;

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    this.hideLoading();
    return this.queryVersion;
  }

  setCachedResult(queryKey, resultsPayload) {
    if (this.cachedResults.size >= 20 && !this.cachedResults.has(queryKey)) {
      const oldestKey = this.cachedResults.keys().next().value;
      this.cachedResults.delete(oldestKey);
    }
    this.cachedResults.set(queryKey, resultsPayload);
  }

  getOptions() {
    if (!this.results) return [];
    return Array.from(this.results.querySelectorAll('[role="option"]')).filter(
      (element) => !element.hidden && element.getClientRects().length > 0
    );
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const options = this.getOptions();
    if (options.length === 0 || (moveUp && !selectedElement)) return;

    const currentIndex = options.indexOf(selectedElement);
    const nextIndex = moveUp
      ? currentIndex <= 0
        ? options.length - 1
        : currentIndex - 1
      : currentIndex < 0 || currentIndex === options.length - 1
        ? 0
        : currentIndex + 1;
    const nextElement = options[nextIndex];

    if (selectedElement) selectedElement.setAttribute('aria-selected', 'false');
    nextElement.setAttribute('aria-selected', 'true');
    this.input.setAttribute('aria-activedescendant', nextElement.id);
    nextElement.scrollIntoView({ block: 'nearest' });
  }

  selectOption() {
    const selectedElement = this.querySelector('[aria-selected="true"]');
    if (!selectedElement) return false;

    const target = selectedElement.matches('a[href], button:not([disabled])')
      ? selectedElement
      : selectedElement.querySelector('a[href], button:not([disabled])');
    if (!target) return false;

    target.click();
    return true;
  }

  renderSearchResults(resultsPayload, searchTerm, queryVersion, sessionToken) {
    if (!this.results || !this.input) return false;
    if (!this.canCommit(queryVersion, searchTerm, sessionToken)) return false;

    const payload =
      typeof resultsPayload === 'string'
        ? { markup: resultsPayload, totalResults: null }
        : resultsPayload || { markup: '', totalResults: 0 };
    const markup = typeof payload.markup === 'string' ? payload.markup : '';

    if (!markup.trim() || payload.totalResults === 0) {
      this.results.innerHTML = '';
      this.removeAttribute('results');
      this.close(false);

      const noResultsText = this.dataset.noResultsText || 'No search results found.';
      this.setErrorState('');
      this.setEmptyState(true);
      this.setLiveRegionText(noResultsText);
      return true;
    }

    this.setEmptyState(false);
    this.setErrorState('');
    this.results.innerHTML = markup;
    this.setAttribute('results', 'true');

    const renderedOptionCount = this.results.querySelectorAll('[role="option"]').length;
    const resultCount = payload.totalResults ?? renderedOptionCount;
    this.setLiveRegionText(this.getResultsStatusText(resultCount));
    return this.open();
  }

  renderSearchError(searchTerm, queryVersion, sessionToken) {
    if (!this.results || !this.input) return false;
    if (!this.canCommit(queryVersion, searchTerm, sessionToken)) return false;

    this.results.innerHTML = '';
    this.removeAttribute('results');
    this.close(false);

    const errorText = this.dataset.errorText || 'Search is temporarily unavailable. Please try again.';
    this.setEmptyState(false);
    this.setErrorState(errorText);
    this.setLiveRegionText(errorText);
    return true;
  }

  getResultsStatusText(resultCount) {
    const count = Number.isFinite(resultCount) ? resultCount : 0;
    const template = this.dataset.resultsText || '[count] search results available.';
    return template.includes('[count]') ? template.replace('[count]', String(count)) : `${count} ${template}`;
  }

  setEmptyState(isEmpty) {
    this.toggleAttribute('empty', isEmpty);
    const modal = this.closest('semu-search-modal');
    if (typeof modal?.syncSearchContent === 'function') modal.syncSearchContent();
  }

  setErrorState(errorText) {
    const hasError = Boolean(errorText);
    this.toggleAttribute('error', hasError);
    if (this.errorStatusText) this.errorStatusText.textContent = hasError ? errorText : '';
    if (this.errorStatus) this.errorStatus.hidden = !hasError;

    const modal = this.closest('semu-search-modal');
    if (typeof modal?.syncSearchContent === 'function') modal.syncSearchContent();
  }

  setLiveRegionText(statusText) {
    if (!this.liveRegion) return;

    const normalizedStatus = String(statusText || '').replace(/\s+/g, ' ').trim();
    if (!normalizedStatus) return;

    this.liveRegion.setAttribute('aria-hidden', 'false');
    this.liveRegion.textContent = normalizedStatus;

    if (this.liveRegionTimer) clearTimeout(this.liveRegionTimer);
    this.liveRegionTimer = setTimeout(() => {
      this.liveRegionTimer = null;
      if (this.liveRegion?.isConnected) this.liveRegion.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  open() {
    if (!this.input || !this.results || !this.isModalOpen() || !this.input.value.trim()) return false;
    this.setAttribute('open', 'true');
    this.input.setAttribute('aria-expanded', 'true');
    return true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.currentSearchTerm = '';
      if (this.input) this.input.value = '';
      if (this.results) this.results.innerHTML = '';
      this.setEmptyState(false);
      this.setErrorState('');
      this.removeAttribute('results');
    }

    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input?.setAttribute('aria-expanded', 'false');
    this.input?.removeAttribute('aria-activedescendant');

    // Remove aria-selected from all options
    this.querySelectorAll('[aria-selected="true"]').forEach((element) => {
      element.setAttribute('aria-selected', 'false');
    });
  }

  suspend() {
    this.invalidatePendingRequest();
    this.close(false);
  }

  reset() {
    this.invalidatePendingRequest();
    this.close(true);
  }

  showLoading(queryVersion = this.queryVersion) {
    if (queryVersion !== this.queryVersion || !this.isModalOpen()) return;
    this.setEmptyState(false);
    this.setErrorState('');
    const loadingElement = this.querySelector('[data-predictive-search-loading]');
    if (loadingElement) {
      loadingElement.style.display = 'flex';
    }
    if (this.results) {
      this.results.setAttribute('data-loading', 'true');
    }
    this.setAttribute('loading', 'true');
  }

  hideLoading(queryVersion = null) {
    if (queryVersion !== null && queryVersion !== this.queryVersion) return;
    const loadingElement = this.querySelector('[data-predictive-search-loading]');
    if (loadingElement) {
      loadingElement.style.display = 'none';
    }
    if (this.results) {
      this.results.removeAttribute('data-loading');
    }
    this.removeAttribute('loading');
  }
}

if (!customElements.get('semu-predictive-search')) {
  customElements.define('semu-predictive-search', SEMUPredictiveSearch);
}

/**
 * Search Modal Component - Full Width Header Search
 */
class SEMUSearchModal extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.isOpening = false;
    this.stateToken = 0;
    this.openRaf = null;
    this.closeTimer = null;
    this.closeTransitionHandler = null;
    this.previouslyFocusedElement = null;
    this.focusTrapHandler = null;
    this._eventsBound = false;
  }

  connectedCallback() {
    if (this._eventsBound) return;
    this.setupModal();
    this.bindEvents();
  }

  disconnectedCallback() {
    this.stateToken += 1;
    this.isOpen = false;
    this.isOpening = false;

    if (this.openRaf) {
      cancelAnimationFrame(this.openRaf);
      this.openRaf = null;
    }

    this.clearCloseWait();
    this.releaseFocus();
    this.predictiveSearch?.suspend();
    this.modal?.classList.remove('is-active');
    this.classList.remove('search-is-active');
    this.hidden = true;
    this.inert = true;
    this.setAttribute('aria-hidden', 'true');
    this.previouslyFocusedElement = null;
    document.body.classList.remove('search-is-open');
    this.updateHeaderState(false);
    this.setTriggerState(false);
    this.unbindEvents();
  }

  setupModal() {
    this.modal = this.querySelector('.semu-search-modal');
    this.backdrop = this.querySelector('[data-search-backdrop]');
    this.closeButton = this.querySelector('[data-search-close]');
    this.form = this.querySelector('[data-search-form]');
    this.input = this.form?.querySelector('input[type="text"], input[type="search"]');
    this.searchContent = this.querySelector('[data-search-content]');
    this.popularSearches = this.querySelectorAll('[data-search-term]');

    // Find predictive search component
    this.predictiveSearch = this.querySelector('semu-predictive-search');
    let predictiveSearchReady = false;
    if (this.predictiveSearch) {
      this.predictiveSearch.input = this.input;
      predictiveSearchReady = this.predictiveSearch.ensureInitialized();
    }

    this.isReady = Boolean(
      this.modal &&
        this.backdrop &&
        this.closeButton &&
        this.form &&
        this.input &&
        this.predictiveSearch &&
        predictiveSearchReady
    );

    if (!this.isReady) {
      console.error('SEMU Search: Required modal elements not found');
    }
  }

  bindEvents() {
    if (this._eventsBound) return;
    this._eventsBound = true;

    this._onOpenRequest = () => this.open();
    this._onCloseRequest = () => this.close();
    this._onDocumentKeydown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        this.open();
      }

      if (event.key === 'Escape' && (this.isOpen || this.isOpening)) {
        event.preventDefault();
        this.close();
      }
    };

    this._onDocumentClick = (event) => {
      const searchTrigger = event.target instanceof Element ? event.target.closest('[data-search-trigger]') : null;
      if (!searchTrigger) return;
      if (!this.isReady) return;

      event.preventDefault();
      this.open();
    };

    this._onRootClick = (event) => {
      if (event.target === this.backdrop || (event.target instanceof Element && event.target.closest('[data-search-close]'))) {
        this.close();
        return;
      }

      const popularSearch =
        event.target instanceof Element ? event.target.closest('[data-search-term]') : null;
      if (!popularSearch || !this.contains(popularSearch)) return;

      const searchTerm = popularSearch.getAttribute('data-search-term');
      if (searchTerm && this.input) {
        this.input.value = searchTerm;
        this.input.dispatchEvent(new Event('input', { bubbles: true }));
        this.input.focus({ preventScroll: true });
      }
    };

    this._onFormSubmit = (event) => {
      if (!this.input?.value.trim()) event.preventDefault();
    };

    this._onModalInput = () => this.syncSearchContent();

    document.addEventListener('semu:search:open', this._onOpenRequest);
    document.addEventListener('semu:search:close', this._onCloseRequest);
    document.addEventListener('keydown', this._onDocumentKeydown);
    document.addEventListener('click', this._onDocumentClick);
    this.addEventListener('click', this._onRootClick);
    this.form?.addEventListener('submit', this._onFormSubmit);
    this.input?.addEventListener('input', this._onModalInput);
  }

  unbindEvents() {
    if (!this._eventsBound) return;
    this._eventsBound = false;

    document.removeEventListener('semu:search:open', this._onOpenRequest);
    document.removeEventListener('semu:search:close', this._onCloseRequest);
    document.removeEventListener('keydown', this._onDocumentKeydown);
    document.removeEventListener('click', this._onDocumentClick);
    this.removeEventListener('click', this._onRootClick);
    this.form?.removeEventListener('submit', this._onFormSubmit);
    this.input?.removeEventListener('input', this._onModalInput);

    this._onOpenRequest = null;
    this._onCloseRequest = null;
    this._onDocumentKeydown = null;
    this._onDocumentClick = null;
    this._onRootClick = null;
    this._onFormSubmit = null;
    this._onModalInput = null;
  }

  syncSearchContent() {
    if (!this.searchContent || !this.input) return;
    const hasQuery = Boolean(this.input.value.trim());
    const hasPredictiveContent = !this.predictiveSearch?.hasAttribute('empty');
    const shouldShow = hasQuery && hasPredictiveContent;
    this.searchContent.classList.toggle('semu-search-content--hidden', !shouldShow);
    this.searchContent.classList.toggle('semu-search-content--visible', shouldShow);
  }

  setTriggerState(isExpanded) {
    document.querySelectorAll('[data-search-trigger]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
    });
  }

  waitForStylesheet() {
    const stylesheet = document.getElementById('SearchModalStyles');

    if (!stylesheet) {
      return Promise.resolve(window.getComputedStyle(this).position === 'fixed');
    }

    if (stylesheet.dataset.loadState === 'loaded' || (stylesheet.sheet && stylesheet.media === 'all')) {
      return Promise.resolve(true);
    }

    if (stylesheet.dataset.loadState === 'error') return Promise.resolve(false);

    return new Promise((resolve) => {
      let settled = false;
      let timeoutId = null;

      const cleanup = () => {
        stylesheet.removeEventListener('load', onLoad);
        stylesheet.removeEventListener('error', onError);
        if (timeoutId) clearTimeout(timeoutId);
      };

      const finish = (ready) => {
        if (settled) return;
        settled = true;
        cleanup();

        if (!ready) {
          resolve(false);
          return;
        }

        requestAnimationFrame(() => resolve(true));
      };

      const onLoad = () => finish(true);
      const onError = () => finish(false);
      stylesheet.addEventListener('load', onLoad, { once: true });
      stylesheet.addEventListener('error', onError, { once: true });
      timeoutId = setTimeout(() => finish(false), 5000);

      if (stylesheet.dataset.loadState === 'loaded' || (stylesheet.sheet && stylesheet.media === 'all')) {
        finish(true);
      } else if (stylesheet.dataset.loadState === 'error') {
        finish(false);
      }
    });
  }

  redirectToSearch() {
    window.location.assign(window.routes?.search_url || '/search');
  }

  getTransitionDuration(element) {
    if (!element) return 0;

    const styles = window.getComputedStyle(element);
    const parseTimeList = (value) =>
      value.split(',').map((time) => {
        const normalized = time.trim();
        const parsed = Number.parseFloat(normalized) || 0;
        return normalized.endsWith('ms') ? parsed : parsed * 1000;
      });

    const durations = parseTimeList(styles.transitionDuration);
    const delays = parseTimeList(styles.transitionDelay);
    const itemCount = Math.max(durations.length, delays.length);
    let maximum = 0;

    for (let index = 0; index < itemCount; index += 1) {
      maximum = Math.max(
        maximum,
        (durations[index % durations.length] || 0) + (delays[index % delays.length] || 0)
      );
    }

    return maximum;
  }

  clearCloseWait() {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }

    if (this.modal && this.closeTransitionHandler) {
      this.modal.removeEventListener('transitionend', this.closeTransitionHandler);
      this.closeTransitionHandler = null;
    }
  }

  hideAfterClose(token) {
    this.clearCloseWait();

    const finish = () => {
      if (token !== this.stateToken || this.isOpen) return;
      this.clearCloseWait();
      this.hidden = true;
    };

    const duration = this.getTransitionDuration(this.modal);
    if (!this.modal || duration === 0) {
      finish();
      return;
    }

    this.closeTransitionHandler = (event) => {
      if (event.target === this.modal && event.propertyName === 'transform') finish();
    };
    this.modal.addEventListener('transitionend', this.closeTransitionHandler);
    this.closeTimer = setTimeout(finish, duration + 100);
  }

  trapFocus() {
    this.releaseFocus();

    const getFocusableElements = () =>
      Array.from(
        this.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => {
        const styles = window.getComputedStyle(element);
        return !element.hidden && !element.closest('[hidden], [inert]') && styles.visibility !== 'hidden' && element.getClientRects().length > 0;
      });

    this.focusTrapHandler = (event) => {
      if (event.key !== 'Tab') return;
      const focusableElements = getFocusableElements();
      if (focusableElements.length === 0) {
        event.preventDefault();
        return;
      }

      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements[focusableElements.length - 1];
      if (event.shiftKey && document.activeElement === firstFocusable) {
        event.preventDefault();
        lastFocusable.focus();
      } else if (!event.shiftKey && document.activeElement === lastFocusable) {
        event.preventDefault();
        firstFocusable.focus();
      }
    };

    this.addEventListener('keydown', this.focusTrapHandler);
  }

  releaseFocus() {
    if (!this.focusTrapHandler) return;
    this.removeEventListener('keydown', this.focusTrapHandler);
    this.focusTrapHandler = null;
  }

  async open() {
    if (this.isOpen || this.isOpening) return false;
    if (!this.isReady) {
      this.redirectToSearch();
      return false;
    }

    this.isOpening = true;
    const token = ++this.stateToken;
    this.clearCloseWait();

    if (this.openRaf) {
      cancelAnimationFrame(this.openRaf);
      this.openRaf = null;
    }

    const stylesReady = await this.waitForStylesheet();
    if (token !== this.stateToken) return false;

    this.isOpening = false;
    if (!stylesReady) {
      this.redirectToSearch();
      return false;
    }

    this.previouslyFocusedElement = document.activeElement;
    this.isOpen = true;
    document.dispatchEvent(new CustomEvent('semu:header:measure'));
    this.hidden = false;
    this.inert = false;
    this.setAttribute('aria-hidden', 'false');
    this.setTriggerState(true);
    this.syncSearchContent();

    void this.modal.offsetWidth;
    this.openRaf = requestAnimationFrame(() => {
      this.openRaf = null;
      if (token !== this.stateToken || !this.isOpen) return;

      this.modal.classList.add('is-active');
      this.classList.add('search-is-active');
      document.body.classList.add('search-is-open');
      this.updateHeaderState(true);
      this.trapFocus();
      this.input.focus({ preventScroll: true });
      document.dispatchEvent(new CustomEvent('semu:search:opened'));
    });

    return true;
  }

  close() {
    if (!this.isOpen && !this.isOpening) return;

    const wasOpen = this.isOpen;
    const token = ++this.stateToken;
    this.isOpen = false;
    this.isOpening = false;

    if (this.openRaf) {
      cancelAnimationFrame(this.openRaf);
      this.openRaf = null;
    }

    this.predictiveSearch?.suspend();
    this.modal?.classList.remove('is-active');
    this.classList.remove('search-is-active');
    document.body.classList.remove('search-is-open');
    this.updateHeaderState(false);
    this.setTriggerState(false);
    this.releaseFocus();
    this.inert = true;
    this.setAttribute('aria-hidden', 'true');

    const focusTarget =
      this.previouslyFocusedElement instanceof HTMLElement &&
      this.previouslyFocusedElement.isConnected &&
      !this.contains(this.previouslyFocusedElement)
        ? this.previouslyFocusedElement
        : document.querySelector('[data-search-trigger]');
    focusTarget?.focus({ preventScroll: true });
    this.previouslyFocusedElement = null;

    if (!wasOpen) {
      this.hidden = true;
      return;
    }

    this.hideAfterClose(token);
    document.dispatchEvent(new CustomEvent('semu:search:closed'));
  }

  updateHeaderState(isOpen) {
    const header = document.querySelector('.header');
    if (header) {
      if (isOpen) {
        header.classList.add('search-active');
      } else {
        header.classList.remove('search-active');
      }
    }
  }
}

if (!customElements.get('semu-search-modal')) {
  customElements.define('semu-search-modal', SEMUSearchModal);
}

// Global search methods
window.SEMU = window.SEMU || {};
window.SEMU.openSearch = () => {
  document.dispatchEvent(new CustomEvent('semu:search:open'));
};
window.SEMU.closeSearch = () => {
  document.dispatchEvent(new CustomEvent('semu:search:close'));
};

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeSearch);
} else {
  initializeSearch();
}

function initializeSearch() {
  if (typeof routes === 'undefined') {
    window.routes = window.routes || {
      search_url: '/search',
      predictive_search_url: '/search/suggest',
    };
  }
}
